/**
 * mood.ts — Mood widget + /mood trend chart — Phase 173
 *
 * Home widget: emoji tap → AJAX-POST to data-log-url → {success:true} → collapse to done state.
 * /mood page: Highcharts spline chart from mood_series server var + backfill form.
 * All mutations POST to signal URL read from dataset.logUrl (never hardcode ?do=).
 * Bootstrap imports directly — NOT window.bootstrap (silent no-op — feedback_bootstrap_not_on_window).
 * No per-chart time: block — useUTC:false is set globally in src/index.ts.
 */

import * as Highcharts from 'highcharts';
import { getChartTheme } from './utils/chartTheme';

// Populated by Mood/default.latte via {json_encode($trendSeries)|noescape}
declare const mood_series: Array<{ x: string; y: number }>;
// Populated by Mood/default.latte via {$logUrl} (Latte JS-context auto-escapes the URL string)
declare const mood_log_url: string;

export default {

    _inited: false,

    init(): void {
        // Idempotent init guard — prevent double-bind if init() is called twice (goals.ts pattern)
        if (this._inited) return;
        this._inited = true;

        this._initBackfill();
        this._initTrendChart();
    },

    // ─── /mood backfill form ─────────────────────────────────────────────────

    _initBackfill(): void {
        const facesEl = document.querySelector<HTMLElement>('.js-mood-backfill-faces');
        if (!facesEl) return;  // not on /mood page

        const logUrl    = facesEl.dataset.logUrl ?? '';
        const dateInput = document.querySelector<HTMLInputElement>('.js-mood-backfill-date');
        const saveBtn   = document.querySelector<HTMLButtonElement>('.js-mood-bf-save');
        const saveLabel = document.querySelector<HTMLElement>('.js-mood-bf-save-label');
        const spinner   = document.querySelector<HTMLElement>('.js-mood-bf-spinner');
        const errorEl   = document.querySelector<HTMLElement>('.js-mood-bf-error');
        const noteToggle  = document.querySelector<HTMLButtonElement>('.js-mood-bf-note-toggle');
        const noteExpand  = document.querySelector<HTMLElement>('.js-mood-bf-note-expand');
        const noteTextarea = document.querySelector<HTMLTextAreaElement>('.js-mood-bf-note');
        const noteCounter = document.querySelector<HTMLElement>('.js-mood-bf-note-counter');

        let selectedScore: number | null = null;

        // Face buttons on /mood page
        facesEl.querySelectorAll<HTMLButtonElement>('.js-mood-bf-face').forEach(btn => {
            btn.addEventListener('click', () => {
                const score = parseInt(btn.dataset.score ?? '0', 10);
                if (!score) return;
                selectedScore = score;

                // Accent ring
                facesEl.querySelectorAll<HTMLButtonElement>('.js-mood-bf-face').forEach(b => {
                    b.style.outline = b === btn ? '2px solid var(--ksat-accent)' : '';
                    b.style.borderRadius = b === btn ? '50%' : '';
                    b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
                });

                // Enable save button
                if (saveBtn) saveBtn.disabled = false;
            });
        });

        // Note toggle
        if (noteToggle && noteExpand) {
            noteToggle.addEventListener('click', () => {
                noteExpand.classList.toggle('d-none');
            });
        }

        // Note counter
        if (noteTextarea && noteCounter) {
            noteTextarea.addEventListener('input', () => {
                noteCounter.textContent = `${noteTextarea.value.length} / 280`;
            });
        }

        // Save button
        if (saveBtn) {
            saveBtn.addEventListener('click', () => {
                if (!selectedScore) return;
                const day  = dateInput?.value ?? new Date().toISOString().slice(0, 10);
                const note = noteTextarea?.value ?? '';

                this._submitPost(logUrl, selectedScore, day, note, {
                    onStart: () => {
                        if (saveBtn)   saveBtn.disabled = true;
                        if (saveLabel) saveLabel.textContent = 'Saving…';
                        if (spinner)   spinner.classList.remove('d-none');
                        if (errorEl)   errorEl.classList.add('d-none');
                        facesEl.querySelectorAll<HTMLButtonElement>('.js-mood-bf-face').forEach(b => { b.disabled = true; });
                    },
                    onSuccess: () => {
                        // Reload so trend chart + recent list refresh (goals.ts page-reload pattern)
                        setTimeout(() => location.reload(), 600);
                    },
                    onError: (msg: string) => {
                        if (errorEl) {
                            errorEl.textContent = msg || 'Could not save. Please try again.';
                            errorEl.classList.remove('d-none');
                        }
                        if (saveBtn)   saveBtn.disabled = false;
                        if (saveLabel) saveLabel.textContent = 'Save';
                        if (spinner)   spinner.classList.add('d-none');
                        facesEl.querySelectorAll<HTMLButtonElement>('.js-mood-bf-face').forEach(b => { b.disabled = false; });
                    },
                });
            });
        }
    },

    // ─── Shared fetch POST helper ─────────────────────────────────────────────

    _submitPost(
        url: string,
        score: number,
        day: string,
        note: string,
        callbacks: {
            onStart: () => void;
            onSuccess: () => void;
            onError: (msg: string) => void;
        }
    ): void {
        callbacks.onStart();

        const body = new URLSearchParams();
        body.set('score', String(score));
        body.set('day',   day);
        body.set('note',  note);

        fetch(url, {
            method:  'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body:    body.toString(),
        })
        .then(r => r.json())
        .then((data: any) => {
            if (data.success) {
                callbacks.onSuccess();
            } else {
                callbacks.onError(data.message ?? 'Could not save. Please try again.');
            }
        })
        .catch(() => {
            callbacks.onError('Could not save. Please try again.');
        });
    },

    // ─── /mood trend chart ────────────────────────────────────────────────────

    _initTrendChart(): void {
        const el = document.getElementById('chart_mood_trend');
        if (!el) return;  // not on /mood page
        if (typeof mood_series === 'undefined') return;
        if (!mood_series.length) return;  // empty-state handled server-side in Latte

        const points = mood_series.map(p => [Date.parse(p.x), p.y]);

        Highcharts.chart('chart_mood_trend', Highcharts.merge(getChartTheme(), {
            chart: { type: 'spline', height: 320 },
            title: { text: undefined },
            legend: { enabled: false },
            xAxis: {
                type: 'datetime',
            },
            yAxis: {
                min: 1,
                max: 5,
                tickInterval: 1,
                title: { text: 'Mood (1–5)' },
                gridLineWidth: 0.1,
                plotLines: [{
                    value:     2.5,
                    dashStyle: 'Dash',
                    color:     '#6c757d',
                    width:     1,
                    label: {
                        text:  'Low mood threshold',
                    },
                }],
            },
            tooltip: { valueDecimals: 0 },
            series: [{
                name:         'Mood',
                color:        '#F9C74F',  // D-01: data-identity color — NOT accent, keep fixed
                data:         points,
                connectNulls: false,
                marker: { enabled: true, radius: 4 },
            }] as any,
            credits: { enabled: false },
        } as Highcharts.Options));
        // NOTE: No per-chart time: block — useUTC:false is set globally in src/index.ts (L44).
    },
};
